import { useEffect, useRef, useState } from 'react';
import { backButton } from '@telegram-apps/sdk-react';
import { useTranslation } from 'react-i18next';

import {
  overlayStyles,
  videoContainerStyles,
  videoElementStyles
} from './styles';

import { MainButton } from '@/components/common/MainButton/MainButton.tsx';
import {
  SelfieRequirementsSections,
  ShowTakenSelfie
} from '@/components/ProfileData/Selfie/parts/parts.tsx';
import { FileData } from '@/shared/interface/userRegistrationData.ts';
import { PhotoKind, UserSelfieProps } from '@/shared/type/userDataFields.ts';

export function AndroidUserSelfie({
  selfie,
  updateFields,
  isSubmitting
}: UserSelfieProps) {
  const { t } = useTranslation();
  const [cameraActive, setCameraActive] = useState<boolean>(false);
  const [overlayVisible, setOverlayVisible] = useState<boolean>(true);
  const [takenSelfie, setTakenSelfie] = useState<FileData | null>(
    selfie || null
  );
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    backButton.onClick(handleStopCamera);
  }, [backButton]);

  useEffect(() => {
    if (cameraActive) {
      startCamera();
    }
    return handleStopCamera;
  }, [cameraActive]);

  const startCamera = () => {
    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then((stream) => {
        setVideoStream(stream);
        const videoElement = videoRef.current;
        if (videoElement) {
          videoElement.srcObject = stream;
          void videoElement.play();
          videoElement.addEventListener('playing', () =>
            setOverlayVisible(false)
          );
        }
      })
      .catch(() => {
        setCameraActive(false);
      });
  };

  const handleTakePhoto = () => {
    const video = videoRef.current;
    if (video) {
      const canvas = document.createElement('canvas');
      canvas.width = 960;
      canvas.height = 1280;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });
          const selfieFile = {
            file,
            url: {
              [PhotoKind.KIND_PRIMARY]: URL.createObjectURL(file),
              [PhotoKind.KIND_THUMB]: URL.createObjectURL(file)
            },
            hid: Date.now().toString()
          };
          setTakenSelfie(selfieFile);
          updateFields({ selfie: selfieFile });
          handleStopCamera();
          setCameraActive(false);
        }
      });
    }
  };

  const handleRetake = () => {
    setTakenSelfie(null);
    updateFields({ selfie: undefined });
    setCameraActive(true);
    setOverlayVisible(true);
  };

  const handleStopCamera = () => {
    videoStream?.getTracks().forEach((track) => track.stop());
  };

  const renderInitialState = () => (
    <>
      <SelfieRequirementsSections />
      <MainButton
        text={t('button.open_camera')}
        fixed
        onClick={() => setCameraActive(true)}
      />
    </>
  );

  const renderCameraState = () => (
    <div style={videoContainerStyles}>
      <video ref={videoRef} autoPlay playsInline style={videoElementStyles} />
      {overlayVisible && (
        <div style={overlayStyles}>
          <p>Loading Camera...</p>
        </div>
      )}
      <MainButton
        text={t('button.take_photo')}
        fixed
        onClick={handleTakePhoto}
      />
    </div>
  );

  return (
    <div>
      {!cameraActive ? (
        takenSelfie ? (
          <ShowTakenSelfie
            takenSelfie={takenSelfie}
            handleRetake={handleRetake}
            isSubmitting={isSubmitting}
          />
        ) : (
          renderInitialState()
        )
      ) : (
        renderCameraState()
      )}
    </div>
  );
}
